/**
 * Availability schemas for GET /media-units/availability.
 *
 * Traceability: REQ-2.15, REQ-4.3, REQ-7.4
 */
import { z } from 'zod';
import { PERIOD_GRANULARITY_VALUES } from '../period-granularity.js';
import { ReservationStatusSchema, UnitBaseStatusSchema } from './reservation-status.js';

// Re-derive from the canonical PERIOD_GRANULARITY_VALUES to avoid a circular import
// with offer-availability.ts (which imports CellStateSchema from this file).
const PeriodGranularitySchema = z.enum(PERIOD_GRANULARITY_VALUES);

// ─── CellState ────────────────────────────────────────────────────────────────
// Union of reservation-row statuses (OPTION|SOFT_RESERVATION|HARD_RESERVATION|SOLD),
// unit base states (FREE|ASK_AVAILABILITY), and the client-view projection POTENTIAL.
// POTENTIAL is NOT a DB-persisted status; the API emits it as the client-view
// projection of ASK_AVAILABILITY entries (viewerKind='client'). It is included
// here so Zod response validation accepts it at the serialization boundary.
export const ClientProjectionStatusSchema = z.enum(['POTENTIAL']);

// The availability-window overlay layer (availability-windows-model). Windows
// sit between the base state and reservations: an UNAVAILABLE window replaces
// a base-state entry outright for the period it covers, when no reservation
// overlaps. FREE windows are not surfaced as a distinct cell state — they
// carry no visual precedence over the existing FREE base state.
export const AvailabilityWindowOverlayStatusSchema = z.enum(['UNAVAILABLE']);

export const CellStateSchema = z.union([
  ReservationStatusSchema,
  UnitBaseStatusSchema,
  ClientProjectionStatusSchema,
  AvailabilityWindowOverlayStatusSchema,
]);
export type CellState = z.infer<typeof CellStateSchema>;

// ─── Ownership overlay ────────────────────────────────────────────────────────
export const OwnershipSchema = z.enum(['mine', 'other']);
export type Ownership = z.infer<typeof OwnershipSchema>;

// ─── Status precedence ────────────────────────────────────────────────────────
// Highest index = most dominant. Used by web for stacking order.
// POTENTIAL is intentionally excluded — it is a client-view projection of
// ASK_AVAILABILITY and never coexists with a persisted reservation row.
export const STATUS_PRECEDENCE = [
  'FREE',
  'ASK_AVAILABILITY',
  'UNAVAILABLE',
  'OPTION',
  'SOFT_RESERVATION',
  'HARD_RESERVATION',
  'SOLD',
] as const satisfies readonly CellState[];

// Compile-time exhaustiveness check: every DB-persisted status must appear in
// STATUS_PRECEDENCE. Adding a new value to ReservationStatusSchema,
// UnitBaseStatusSchema, or AvailabilityWindowOverlayStatusSchema without
// updating STATUS_PRECEDENCE is a type error.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _DbStatusesCoveredByPrecedence =
  Exclude<
    | z.infer<typeof ReservationStatusSchema>
    | z.infer<typeof UnitBaseStatusSchema>
    | z.infer<typeof AvailabilityWindowOverlayStatusSchema>,
    (typeof STATUS_PRECEDENCE)[number]
  > extends never
    ? true
    : 'Missing DB status in STATUS_PRECEDENCE — add it to the ordered array above';

// ─── CellEntry ────────────────────────────────────────────────────────────────
// One coexisting reservation (or base state) within a period cell.
// Label fields are present only when the caller has offer view access.
export const CellEntrySchema = z.object({
  status: CellStateSchema,
  ownership: OwnershipSchema.optional(), // omitted on FREE/ASK_AVAILABILITY base-state entries
  reservationId: z.number().int().positive().optional(), // present on own-held entries (ownership='mine'), or on others' entries when the caller holds a quick-action permission on the reservation
  clientName: z.string().optional(),
  holderName: z.string().optional(),
  conflictingOfferId: z.number().int().positive().optional(),
  offerTitle: z.string().optional(),
  conditional: z.boolean().optional(), // only on HARD_RESERVATION rows
  ttlExpiresAt: z.string().nullable().optional(), // SOFT/HARD_RESERVATION TTL; null for OPTION/SOLD/base-state
  // The reservation's own booking window (its first/last booked day) — present on every
  // entry backed by a reservation row, omitted for base-state and availability-window
  // entries (which have no reservation). Not gated by ownership or the label-authorization
  // rule above, same reasoning as ttlExpiresAt; stripped entirely for viewerKind='client'.
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  fromAvailabilityWindow: z.boolean().optional(), // present when this entry is an availability-window overlay
});
export type CellEntry = z.infer<typeof CellEntrySchema>;

// ─── AvailabilityPeriodCell ───────────────────────────────────────────────────
// One period slot for a unit. entries[] contains all coexisting reservations.
// Single-entry array when only the base state applies (FREE or ASK_AVAILABILITY, no reservation).
export const AvailabilityPeriodCellSchema = z.object({
  start: z.string().date(),
  end: z.string().date(),
  entries: z.array(CellEntrySchema).min(1),
});
export type AvailabilityPeriodCell = z.infer<typeof AvailabilityPeriodCellSchema>;

// ─── MediaUnitAvailabilityRow ─────────────────────────────────────────────────
export const MediaUnitAvailabilityRowSchema = z.object({
  mediaUnitId: z.number().int().positive(),
  granularity: PeriodGranularitySchema,
  periods: z.array(AvailabilityPeriodCellSchema),
});
export type MediaUnitAvailabilityRow = z.infer<typeof MediaUnitAvailabilityRowSchema>;

// ─── Request / Response ───────────────────────────────────────────────────────
export const GetMediaUnitsAvailabilityRequestSchema = z.object({
  unitIds: z.array(z.number().int().positive()).min(1).max(200),
  from: z.string().date(),
  to: z.string().date(),
  defaultGranularity: PeriodGranularitySchema.optional(),
});
export type GetMediaUnitsAvailabilityRequest = z.infer<
  typeof GetMediaUnitsAvailabilityRequestSchema
>;

export const GetMediaUnitsAvailabilityResponseSchema = z.object({
  units: z.array(MediaUnitAvailabilityRowSchema),
});
export type GetMediaUnitsAvailabilityResponse = z.infer<
  typeof GetMediaUnitsAvailabilityResponseSchema
>;
